import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { RoutesLink } from '@/utility/RouteLinks';
import { useEffect, useState } from 'react';
import EmailIcon from '@/components/Icons/EmailIcons';
import PasswordIcon from '@/components/Icons/PasswordIcons';
import EyeCloseIcon from '@/components/Icons/EyeCloseIcon';
import EyeOpenIcon from '@/components/Icons/EyeOpenIcons';
import { ILogin, ILoginData } from '@/interfaces/auth.model';
import { v4 as uuidv4 } from 'uuid';
import { callApi, IApiErrorResponse } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import {
  authChannel,
  // getRedirectUri,
  setCookies,
  showToastNotification,
} from '@/utility/helper';
import { useDispatch } from 'react-redux';
import { setUserData } from '@/redux/features/UserSlice';
import { IUser } from '@/interfaces/user.model';
import Button from '@/components/ui/Button';
import PrimaryInput from '@/components/ui/PrimaryInput';
import { PublicClientApplication } from '@azure/msal-browser';
import { loginRequest, msalConfig } from '@/authConfig';
import AuthLayout from '@/components/Layout/AuthLayout';
import { errorTypes } from '@/utility/validation';
import ForgetPassword from './ForgetPassword';
import NewPassword from './NewPassword';
import { requestForToken } from '@/firebase-messaging';
interface IToken {
  accessToken: string;
  refreshToken: string;
}
interface ILoginResponse {
  status: boolean;
  data: {
    user: IUser;
    token: IToken;
  };
  message: string;
}
const Login: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [outlookLoading, setOutlookLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState(1);
  const [fcmToken, setFCMToken] = useState<string | null>(null);
  const dispatch = useDispatch();
  const randomDeviceId = uuidv4();
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ILogin>();
  const msalInstance = new PublicClientApplication(msalConfig);

  useEffect(() => {
    getFirebaseData();
  }, []);
  const getFirebaseData = async () => {
    const token = await requestForToken();
    if (token) {
      setFCMToken(token);
      localStorage.setItem(import.meta.env.VITE_BUSINESS + 'fcmToken', token);
    }
  };
  const onSubmit: SubmitHandler<ILogin> = async (data) => {
    try {
      await msalInstance.initialize();

      let loginResponse = null;
      try {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
          loginResponse = await msalInstance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
          });
        }
      } catch (e) {
        console.log('Silent token acquisition failed', e);
      }

      const token = loginResponse?.accessToken;
      let deviceId = '';
      if (localStorage.getItem(import.meta.env.VITE_BUSINESS + 'deviceId')) {
        deviceId =
          localStorage.getItem(import.meta.env.VITE_BUSINESS + 'deviceId') ??
          '';
      } else {
        deviceId = '2025_' + randomDeviceId;
      }
      localStorage.setItem(
        import.meta.env.VITE_BUSINESS + 'deviceId',
        deviceId,
      );
      const loginData: ILoginData = {
        email: data?.email?.trim(),
        password: data?.password?.trim(),
        deviceId: deviceId,
        deviceToken: fcmToken,
        from: 'emailPassword',
        teamUserId: loginResponse?.correlationId,
      };
      const API = `${API_URL}${API_ENDPOINT?.login}`;
      setIsLoading(true);
      const response: ILoginResponse = await callApi(API, {
        method: 'POST',
        data: loginData,
      });
      if (response?.status && 'data' in response) {
        const data = response?.data?.user as IUser;
        showToastNotification('success', response?.message);
        setCookies(
          import.meta.env.VITE_BUSINESS + 'token',
          response?.data?.token?.accessToken,
        );
        setCookies(
          import.meta.env.VITE_BUSINESS + 'refreshToken',
          response?.data?.token?.refreshToken,
        );
        localStorage.setItem(
          import.meta.env.VITE_BUSINESS + 'token',
          token as string,
        );
        localStorage.setItem(
          import.meta.env.VITE_BUSINESS + '_teamToken',
          token as string,
        );
        dispatch(setUserData(data));
        navigate(RoutesLink?.dashboard);
        authChannel.postMessage('login');
        setIsLoading(false);
      } else {
        showToastNotification('danger', response?.message);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleOutlookLogin = async () => {
    try {
      setOutlookLoading(true);
      await msalInstance.initialize();

      const loginResponse = await msalInstance.loginPopup(loginRequest);

      const token = loginResponse?.idToken;

      let deviceId = localStorage.getItem(
        import.meta.env.VITE_BUSINESS + 'deviceId',
      );
      if (!deviceId) {
        deviceId = '2025_' + randomDeviceId;
        localStorage.setItem(
          import.meta.env.VITE_BUSINESS + 'deviceId',
          deviceId,
        );
      }

      const loginData: ILoginData = {
        code: token,
        deviceId,
        deviceToken: fcmToken,
        from: 'outlook',
        teamUserId: loginResponse?.correlationId,
      };
      const API = `${API_URL}${API_ENDPOINT?.login}`;
      const response: ILoginResponse = await callApi(API, {
        method: 'POST',
        data: loginData,
      });
      if (response?.status && 'data' in response) {
        const data = response?.data?.user as IUser;
        showToastNotification('success', response?.message);
        setCookies(
          import.meta.env.VITE_BUSINESS + 'token',
          response?.data?.token?.accessToken,
        );
        setCookies(
          import.meta.env.VITE_BUSINESS + 'refreshToken',
          response?.data?.token?.refreshToken,
        );
        localStorage.setItem(
          import.meta.env.VITE_BUSINESS + 'token',
          token as string,
        );
        localStorage.setItem(
          import.meta.env.VITE_BUSINESS + '_teamToken',
          loginResponse?.accessToken as string,
        );
        dispatch(setUserData(data));
        authChannel.postMessage('login');
        setOutlookLoading(false);
        navigate(RoutesLink?.dashboard);
      } else {
        showToastNotification('danger', response?.message);
      }
    } catch (error) {
      if (error && typeof error === 'object' && 'message' in error) {
        showToastNotification('danger', (error as IApiErrorResponse).message);
      } else {
        showToastNotification('danger', 'Something went wrong');
      }
    } finally {
      setOutlookLoading(false);
    }
  };

  return (
    <>
      <AuthLayout>
        <div className="p-md-4 p-xl-5 p-3 auth">
          {activeTab == 1 && (
            <>
              <h1 className="title ">Sign-In</h1>
              <p className="fs-5 mb-5 welcome-login">Welcome back!!</p>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="form-group mb-3">
                  <PrimaryInput
                    label="Email"
                    type="text"
                    placeholder="Enter email"
                    {...register('email', {
                      ...errorTypes.required('Email'),
                      ...errorTypes.email,
                    })}
                    onChange={(e) => {
                      const trimmedEmail = e.target.value.trim().toLowerCase();
                      setValue('email', trimmedEmail, {
                        shouldValidate: true,
                      });
                    }}
                    onBlur={(e) => {
                      const trimmedEmail = e.target.value.trim();
                      setValue('email', trimmedEmail, {
                        shouldValidate: true,
                      });
                    }}
                    startIcon={<EmailIcon />}
                    errors={errors?.email?.message}
                  />
                </div>
                <div className="form-group mb-4">
                  <PrimaryInput
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    style={{ paddingRight: '30px' }}
                    placeholder={'Enter password'}
                    {...register('password', {
                      required: 'Password is required',
                    })}
                    errors={errors?.password?.message}
                    startIcon={<PasswordIcon />}
                    passwordShow={
                      <div
                        className="password-show"
                        onClick={togglePasswordVisibility}
                        style={{ cursor: 'pointer' }}
                      >
                        <span
                          // type="button"
                          className=""
                          style={{ fontSize: '14px', cursor: 'pointer' }}
                        >
                          {showPassword ? <EyeOpenIcon /> : <EyeCloseIcon />}
                        </span>
                      </div>
                    }
                  />
                </div>
                <div className="d-flex align-items-center justify-content-end mb-md-5 mb-4">
                  <div
                    className="btn btn-link p-0 text-decoration-none"
                    onClick={() => {
                      setActiveTab(2);
                    }}
                  >
                    Forget Password ?
                  </div>
                </div>
                <div className="d-grid gap-3 btns ">
                  <Button
                    isLoading={isLoading}
                    disabled={isLoading}
                    type="submit"
                    className="btn btn-primary w-100 p-2.5 fs-4 fw-bold text-white"
                  >
                    Sign-in
                  </Button>
                  <div className="d-flex align-items-center my-2">
                    <div
                      className="flex-grow-1"
                      style={{ borderBottom: '1px solid #ddd' }}
                    ></div>
                    <span
                      className="px-3 text-muted"
                      style={{ fontSize: '12px', fontWeight: 500 }}
                    >
                      Or
                    </span>
                    <div
                      className="flex-grow-1"
                      style={{ borderBottom: '1px solid #ddd' }}
                    ></div>
                  </div>

                  <Button
                    type="button"
                    isLoading={outlookLoading}
                    disabled={outlookLoading}
                    onClick={handleOutlookLogin}
                    className="btn btn-microsoft d-flex align-items-center justify-content-center gap-2 p-2 fs-5 w-100"
                  >
                    <img
                      src="/assets/images/microsoft.png"
                      className="outlook w-10 p-2"
                    />
                    Sign-in with Microsoft
                  </Button>
                </div>
              </form>
            </>
          )}
          {activeTab == 2 && (
            <ForgetPassword setActiveTab={setActiveTab} token={fcmToken} />
          )}

          {activeTab == 4 && <NewPassword />}
        </div>
      </AuthLayout>
    </>
  );
};

export default Login;
